import { useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import ReactEChartsCore from 'echarts-for-react/lib/core'
import { cn } from '@artprov/design-system'
import { echarts } from './echartsRegistry'
import { buildDonutChartOption } from './buildDonutChartOption'

const resolveChartHeight = (height) => {
  if (typeof height === 'number') {
    return height
  }

  if (typeof height === 'string' && /^\d+(\.\d+)?px$/.test(height.trim())) {
    return Number.parseFloat(height)
  }

  return undefined
}

export const DonutChart = ({
  data,
  height = 420,
  width = '100%',
  minWidth = 450,
  className = '',
  showLegend = false,
  showToolbox = false,
  innerRadius,
  outerRadius,
  labelEdgeDistance,
  title,
  leftHint,
  rightHint,
  showLeftHint = true,
  showRightHint = true,
  showBorder = true,
  disableHoverAnimation = false,
  showTooltip = true,
  showRule = false,
  forceLeftLabelTextAlign = true,
  singleLineLabel = false,
  labelVerticalPadding = 3,
  labelHorizontalPadding = 2,
  labelMaxLength,
  hintOffsetY = 0,
  ariaLabel = 'Donut chart',
}) => {
  const containerRef = useRef(null)
  const chartRef = useRef(null)
  const chartHeight = resolveChartHeight(height)
  const option = buildDonutChartOption({
    data,
    showLegend,
    showToolbox,
    innerRadius,
    outerRadius,
    labelEdgeDistance,
    title,
    leftHint,
    rightHint,
    showLeftHint,
    showRightHint,
    disableHoverAnimation,
    showTooltip,
    showRule,
    forceLeftLabelTextAlign,
    singleLineLabel,
    labelVerticalPadding,
    labelHorizontalPadding,
    labelMaxLength,
    chartHeight,
    hintOffsetY,
  })

  useEffect(() => {
    const container = containerRef.current

    if (!container) return

    const scheduleResize = () => {
      const raf = window.requestAnimationFrame
        ? window.requestAnimationFrame.bind(window)
        : (callback) => {
            callback(0)
            return 0
          }

      raf(() => {
        chartRef.current?.getEchartsInstance?.()?.resize()
      })
    }

    let resizeObserver

    if (typeof ResizeObserver === 'function') {
      resizeObserver = new ResizeObserver(() => {
        scheduleResize()
      })
      resizeObserver.observe(container)
    } else {
      window.addEventListener('resize', scheduleResize)
    }

    return () => {
      resizeObserver?.disconnect()
      window.removeEventListener('resize', scheduleResize)
    }
  }, [])

  return (
    <div
      className={cn(className)}
      style={
        showBorder
          ? {
              border: '1px solid #E3E3E2',
              boxSizing: 'border-box',
            }
          : undefined
      }
    >
      <figure ref={containerRef} className="relative" style={{ width, minWidth, height }}>
        <ReactEChartsCore
          ref={chartRef}
          echarts={echarts}
          option={option}
          notMerge
          lazyUpdate
          autoResize
          opts={{ renderer: 'svg' }}
          style={{ width: '100%', height: '100%' }}
        />
        <figcaption className="sr-only">{ariaLabel}</figcaption>
      </figure>
    </div>
  )
}

DonutChart.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      value: PropTypes.number.isRequired,
      color: PropTypes.string.isRequired,
      selected: PropTypes.bool,
      labelSide: PropTypes.oneOf(['left', 'right']),
    })
  ).isRequired,
  height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  minWidth: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  className: PropTypes.string,
  showLegend: PropTypes.bool,
  showToolbox: PropTypes.bool,
  innerRadius: PropTypes.number,
  outerRadius: PropTypes.number,
  labelEdgeDistance: PropTypes.number,
  title: PropTypes.string,
  leftHint: PropTypes.string,
  rightHint: PropTypes.string,
  showLeftHint: PropTypes.bool,
  showRightHint: PropTypes.bool,
  showBorder: PropTypes.bool,
  disableHoverAnimation: PropTypes.bool,
  showTooltip: PropTypes.bool,
  showRule: PropTypes.bool,
  forceLeftLabelTextAlign: PropTypes.bool,
  singleLineLabel: PropTypes.bool,
  labelVerticalPadding: PropTypes.number,
  labelHorizontalPadding: PropTypes.number,
  labelMaxLength: PropTypes.number,
  hintOffsetY: PropTypes.number,
  ariaLabel: PropTypes.string,
}

export default DonutChart
